// weeklySqftCompleted.js — Modal content for Weekly SQFT Completed KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/weeklySqftCompleted.js

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import {
  WEEKLY_SQFT_HISTORY,
  SQFT_BY_BUILDING,
} from "../../../mockData/mockProductionMilestonesData";

const fmtSqft = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#cc0000" }}
        />
        {payload[0].value.toLocaleString()} sqft
      </div>
    </div>
  );
};

const WeeklySqftCompleted = ({ goals }) => {
  const target = goals?.weeklySqft || 20000;
  const thisWeek = WEEKLY_SQFT_HISTORY[WEEKLY_SQFT_HISTORY.length - 1];
  const avgSqft = Math.round(
    WEEKLY_SQFT_HISTORY.reduce((s, d) => s + d.sqft, 0) /
      WEEKLY_SQFT_HISTORY.length,
  );
  const weeksOnTarget = WEEKLY_SQFT_HISTORY.filter(
    (d) => d.sqft >= target,
  ).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--3">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">This Week</span>
          <span
            className={`pml-modal-stat-value ${thisWeek.sqft >= target ? "pml-modal-stat-value--ok" : "pml-modal-stat-value--alert"}`}
          >
            {thisWeek.sqft.toLocaleString()}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weekly Target</span>
          <span className="pml-modal-stat-value">
            {target.toLocaleString()}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">8-Week Avg</span>
          <span
            className={`pml-modal-stat-value ${avgSqft >= target ? "pml-modal-stat-value--ok" : "pml-modal-stat-value--amber"}`}
          >
            {avgSqft.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Bar chart */}
      <p className="pml-modal-sub-title">8-Week SQFT Trend</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={WEEKLY_SQFT_HISTORY}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={fmtSqft}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={target}
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              label={{
                value: "Target",
                position: "insideTopRight",
                fontSize: 11,
                fill: "#1a1a1a",
              }}
            />
            <Bar dataKey="sqft" radius={[4, 4, 0, 0]}>
              {WEEKLY_SQFT_HISTORY.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.sqft >= target ? "#38a169" : "#cc0000"}
                  fillOpacity={0.8}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-building breakdown */}
      <p className="pml-modal-sub-title">This Week by Building</p>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {SQFT_BY_BUILDING.map((b) => {
          const pct = Math.min(100, Math.round((b.sqft / b.target) * 100));
          const color =
            pct >= 100 ? "#38a169" : pct >= 85 ? "#d69e2e" : "#cc0000";
          return (
            <div key={b.building}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}
                >
                  {b.building}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color }}>
                  {b.sqft.toLocaleString()} / {b.target.toLocaleString()} sqft (
                  {pct}%)
                </span>
              </div>
              <div className="pml-progress-bar">
                <div
                  className="pml-progress-fill"
                  style={{ width: `${pct}%`, background: color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 2 }}>
        {weeksOnTarget} of 8 weeks met the weekly target of{" "}
        {target.toLocaleString()} sqft.
      </div>
    </div>
  );
};

export default WeeklySqftCompleted;
